import { useState } from "react";
import { Search, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fmt } from "@/lib/api";

const SOURCES = [["yt", "YouTube"], ["sc", "SoundCloud"]];

export default function AddSong({ guild, canPlay, onPlay }) {
  const [q, setQ] = useState("");
  const [source, setSource] = useState("yt");
  const [channel, setChannel] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(null);
  const connected = guild?.player?.connected;
  const chan = channel || (connected ? guild.player.channel_id : "");

  const play = async (query) => {
    if (!query.trim()) return;
    setBusy("play");
    try {
      await onPlay({ query, channel_id: chan || null, soundcloud: source === "sc" });
      setQ("");
      setResults([]);
    } finally {
      setBusy(null);
    }
  };

  const search = async () => {
    if (!q.trim()) return;
    setBusy("search");
    try {
      const r = await api.search(q, source);
      setResults(r.tracks);
      if (!r.tracks.length) toast.error("Nic nie znaleziono");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="add-song-panel">
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); canPlay ? play(q) : search(); }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="add-song-input"
          placeholder="Link YouTube / Spotify / SoundCloud lub nazwa utworu…"
          className="flex-1 min-w-0 h-11 rounded-lg bg-slate-950/70 border border-slate-800 focus:border-emerald-500/70 focus:outline-none px-4 text-sm placeholder:text-slate-600 transition-colors"
        />
        <button type="button" onClick={search} data-testid="search-song-button" title="Szukaj"
          className="h-11 w-11 grid place-items-center rounded-lg border border-slate-700 bg-slate-900 hover:bg-slate-800 transition-colors">
          {busy === "search" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </button>
        {canPlay && (
          <button type="submit" data-testid="add-song-submit-button" disabled={busy === "play"}
            className="h-11 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center gap-1.5 transition-colors disabled:opacity-50">
            {busy === "play" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Graj
          </button>
        )}
      </form>
      <div className="flex gap-2">
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-36 bg-slate-950/70 border-slate-800" data-testid="search-source-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {SOURCES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`source-option-${v}`}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        {canPlay && (
          <Select value={chan} onValueChange={setChannel}>
            <SelectTrigger className="flex-1 bg-slate-950/70 border-slate-800" data-testid="voice-channel-select">
              <SelectValue placeholder="Wybierz kanał głosowy" />
            </SelectTrigger>
            <SelectContent>
              {guild?.voice_channels?.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`voice-channel-option-${c.id}`}>🔊 {c.name} ({c.members})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {results.length > 0 && (
        <ul className="divide-y divide-slate-800/80 rounded-lg border border-slate-800 bg-slate-950/40 max-h-72 overflow-y-auto" data-testid="search-results">
          {results.map((t, i) => (
            <li key={t.uri + i} className="flex items-center gap-3 p-2.5 hover:bg-slate-800/40 transition-colors" data-testid={`search-result-${i}`}>
              {t.artwork ? <img src={t.artwork} alt="" className="h-10 w-14 rounded object-cover" /> : <div className="h-10 w-14 rounded bg-slate-800" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm truncate">{t.title}</div>
                <div className="text-xs text-slate-500 truncate">{t.author} · {fmt(t.length)}</div>
              </div>
              {canPlay && (
                <button onClick={() => play(t.uri)} data-testid={`search-result-play-${i}`}
                  className="h-8 w-8 grid place-items-center rounded-full bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/30 transition-colors">
                  <Plus className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
